import { css } from '@emotion/react';
import React, { useState, useRef, useEffect } from 'react';
import { getMentoringProgramGoalVideoPlayStatus } from 'redux/selectors/mentorship-program-goal-items';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { primary } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { playMentorshipProgramGoalsVideoModal } from 'redux/actions/mentoring-program-goal-items';

type VideoPreviewModalProps = {
  videoSrc: string;
  thumbnailSrc: string;
  title: string;
  width?: number;
  height?: number;
  dataQa?: string;
  dataQaId?: string;
  ariaLabel?: string;
};

const styles = (width: number, height: number) => css`
  .thumbnail-wrapper {
    position: relative;
    cursor: pointer;
    transition: transform 0.2s;
    overflow: hidden;
    border-radius: ${quarterSpacing}px;
    width: ${width}px;
    height: ${height}px;
  }
  .thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover,
    border-radius: 8px,
  }
  .thumbnail-wrapper:hover {
    transform: scale(1.02);
  }
  .play-button-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.9;
    transition: opacity 0.3s;
  }
  .thumbnail-wrapper:hover .play-button-overlay {
    opacity: 1;
  }
  .play-button-bg {
    fill: rgba(0,0,0,0.7);
    transition: fill 0.3s ease;
  }
  .thumbnail-wrapper:hover .play-button-bg {
    fill: ${primary};
  }
`;

const VideoPreviewModal = ({
  videoSrc,
  thumbnailSrc,
  title,
  width,
  height,
  dataQa,
  dataQaId,
  ariaLabel,
}: VideoPreviewModalProps) => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playVideo = useSelector(getMentoringProgramGoalVideoPlayStatus);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    if (playVideo) {
      setIsModalOpen(true);
      dispatch(playMentorshipProgramGoalsVideoModal({ play: false }));
    }
  }, [playVideo]);

  return (
    <div css={styles(width, height)} className='video-preview-container'>
      <div
        className='thumbnail-wrapper'
        onClick={openModal}
        data-qa={dataQa}
        data-qa-id={dataQaId}
        aria-label={ariaLabel}
      >
        <img
          src={thumbnailSrc}
          alt={title}
          className='thumbnail-image'
        />
        <div className='play-button-overlay'>
          <svg width='40' height='40' viewBox='0 0 24 24' fill='none'>
            <circle cx='12' cy='12' r='11' fill='rgba(0,0,0,0.7)' className='play-button-bg' />
            <path d='M9 7L17 12L9 17V7Z' fill='white' />
          </svg>
        </div>
      </div>
      {isModalOpen && (
        <NvModal
          show
          width={995}
          height='unset'
          type={ModalType.FIXED}
          header={title}
          doubleModal
          body={(
            <video
              ref={videoRef}
              src={videoSrc}
              controls
              autoPlay
              className='modal-video'
              style={{ width: '100%' }}
            />
          )}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default VideoPreviewModal;
